import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect, useRouteMatch, useHistory } from 'react-router-dom';
import fetch from 'cross-fetch';
import { NS } from '../../../models/I18n';
import Button from '../../../shared/components/buttons/Button';
import * as apiClient from '../../apiClient';
import Link from '../../../shared/components/links/Link';
import { SuperVisasSideBar } from '../components/CrsSection';
import { useSubmissionResultQuery, useUserInfoQuery } from '../../SubmissionAPI';
import { ReactPhoneInput } from '../../../shared/lib/react-phone-input-2';

export function LeadInfoView() {
  const [isLoading, setIsLoading] = useState(false);
  const history = useHistory();
  const ref = useRef<HTMLFormElement>(null);
  const { t } = useTranslation(NS.Eligibility);
  const match = useRouteMatch<{ submissionId: string }>('/eligibility/:submissionId/contact_info');
  const submissionId = match?.params.submissionId || '';

  const { data: submissionResult } = useSubmissionResultQuery(submissionId);
  const { data: userInfoResult } = useUserInfoQuery();

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    setIsLoading(true);
    e.preventDefault();
    if (ref?.current && match) {
      window.gtag?.('event', 'generate_lead', {
        event_category: 'engagement',
        event_label: 'eligibility',
      });
      const data = new FormData(ref.current);
      const firstName = data.get('first_name') as string;
      const lastName = data.get('last_name') as string;
      const email = data.get('email') as string;
      const phoneNumber = data.get('phone_number') as string;
      const leadInfo = {
        first_name: firstName,
        last_name: lastName,
        email,
        phone_number: phoneNumber,
      };
      await apiClient.createLeadAndBindToSubmission({
        leadInfo,
        submissionToken: submissionId,
      });
      if (process.env.NODE_ENV === 'production') {
        const isBrowser = typeof window !== 'undefined';
        const pageUri = isBrowser ? window.location.href : null;
        const pageName = isBrowser ? document.title : null;
        const postUrl =
          'https://api.hsforms.com/submissions/v3/integration/submit/9408879/8b3638dd-e9d3-4df3-89a2-0cf62c096b8d';
        const body = {
          submittedAt: Date.now(),
          fields: [
            {
              name: 'firstname',
              value: firstName,
            },
            {
              name: 'lastname',
              value: lastName,
            },
            {
              name: 'email',
              value: email,
            },
            {
              name: 'phone',
              value: phoneNumber,
            },
            { name: 'intended_country', value: submissionResult?.submission.country },
            { name: 'intended_program_type', value: submissionResult?.submission.lead_intention },
          ],
          context: {
            pageUri,
            pageName,
          },
          legalConsentOptions: {
            consent: {
              // Include this object when GDPR options are enabled
              consentToProcess: true,
              text: 'I agree to the Privacy Policy',
            },
          },
        };
        fetch(postUrl, {
          method: 'post',
          body: JSON.stringify(body),
          headers: {
            'Content-Type': 'application/json',
          },
        });
      }
      history.push(`/eligibility/${submissionId}/book_a_time`);
    }
  };

  // when a lead or user is associated with a submission or the user is logged in, redirect to book a time
  if (submissionResult?.lead || submissionResult?.user || userInfoResult?.user_info) {
    return <Redirect to={`/eligibility/${submissionId}/book_a_time`} />;
  }

  return (
    <div className="block lg:flex">
      <SuperVisasSideBar />
      <div className="w-full bg-white px-8 py-3 lg:min-h-screen lg:w-3/4 lg:pr-24">
        <div className="mb-2 text-base font-semibold text-gray-600 lg:text-2xl">{t('leadActions.schedule-a-call')}</div>
        <div className="mb-3 text-base text-gray-600">{t('leadActions.give-us-your-info')}</div>
        <form ref={ref} onSubmit={onSubmit}>
          <div className="block lg:flex lg:justify-between lg:gap-8">
            <div className="w-full">
              <label className="mb-1 inline-block text-gray-600">{t('leadInfo.first-name')}</label>
              <input
                id="first_name"
                name="first_name"
                className="mb-6 block w-full rounded border-gray-400 text-gray-600 lg:mb-0"
                type="text"
                required
              />
            </div>
            <div className="w-full">
              <label className="mb-1 inline-block text-gray-600">{t('leadInfo.last-name')}</label>
              <input
                id="last_name"
                name="last_name"
                className="block w-full rounded border-gray-400 text-gray-600"
                type="text"
                required
              />
            </div>
          </div>
          <div className="my-6">
            <label className="mb-1 inline-block text-gray-600">{t('leadInfo.email-address')}</label>
            <input
              id="email"
              name="email"
              className="block w-full rounded border-gray-400 text-gray-600"
              type="email"
              required
            />
          </div>
          <div className="mb-6 flex justify-between lg:gap-8">
            <div className="w-full">
              <label className="mb-1 inline-block text-gray-600">{t('leadInfo.phone-number')}</label>
              <ReactPhoneInput
                inputStyle={{ width: '100%', height: 42, border: '1px solid rgb(156 163 175)' }}
                inputProps={{ required: true, name: 'phone_number' }}
                country="ca"
              />
            </div>
            <div className="w-0 lg:w-full" />
          </div>
          <section className="flex justify-end gap-2">
            <Link variant="secondary" to={`/eligibility/${submissionId}/result`}>
              {t('leadInfoView.back')}
            </Link>
            <Button isLoading={isLoading} type="submit">
              {t('leadInfoView.next')}
            </Button>
          </section>
        </form>
      </div>
    </div>
  );
}
